"use client";

import { useEffect, useId, useMemo, useRef, useState } from "react";
import { Field, TextInput } from "@/components/tools/ui";
import {
  BARRIER_STYLE,
  COMPOSITE_STENCIL_GROUPS,
  ELEMENT_COLORS,
  H,
  ICON_CATEGORIES,
  ICON_META,
  LEGEND_WIDTH,
  PLAN_PRESETS,
  ROUTE_STYLE,
  W,
  ZONE_STYLE,
  allocElId,
  findCalibration,
  findCompositeStencil,
  formatMetres,
  hitTest,
  imageFileToBackground,
  imageFileToElement,
  instantiateCompositeStencil,
  metresPerUnit,
  moveElement,
  toLocal,
  type BarrierType,
  type CalibrationEl,
  type CompositeStencil,
  type IconType,
  type LabelEl,
  type LineEl,
  type MeasureEl,
  type PlanBackground,
  type PlanElement,
  type RouteType,
  type ShapeType,
  type Tool,
  type ZoneType,
} from "@/components/tools/siteplan/model";
import { IconSwatch, PlanLegend, PlanSvg } from "@/components/tools/siteplan/PlanSvg";

/* ── Site plan editor ──────────────────────────────────────────────────────
   Toolbar, canvas interactions (draw / select / resize / rotate / zoom) and
   the contextual selection panel. Rendering is shared with print via
   PlanSvg; this file owns everything interactive. */

const MIN_SIZE = 16;
const MAX_ZOOM = 8;
const HISTORY_CAP = 50;
const RECENT_ICON_STORE = "ramsdocs.siteplan.recentIcons";

type BoxHandle = "nw" | "n" | "ne" | "e" | "se" | "s" | "sw" | "w";

type PointerAction =
  | { mode: "move"; id: number; start: { x: number; y: number }; origin: PlanElement }
  | { mode: "resize"; id: number; handle: BoxHandle; start: { x: number; y: number }; origin: PlanElement }
  | { mode: "rotate"; id: number; origin: PlanElement }
  | { mode: "endpoint"; id: number; which: 1 | 2; origin: PlanElement }
  | { mode: "vertex"; id: number; index: number; origin: PlanElement }
  | { mode: "iconsize"; id: number; origin: PlanElement }
  | { mode: "radius"; id: number; origin: PlanElement }
  | { mode: "pan"; startClient: { x: number; y: number }; originPan: { x: number; y: number } };

/* ── Tool metadata ───────────────────────────────────────────────────────── */

const TOOL_HINTS: { match: (t: Tool) => boolean; hint: string }[] = [
  { match: (t) => t === "select", hint: "Click an element to select it. Drag to move; use the handles to resize or rotate." },
  { match: (t) => t === "pan", hint: "Drag to move around the canvas. Use the zoom buttons or Ctrl + scroll." },
  { match: (t) => t === "draw", hint: "Click and drag to draw freehand. Release to finish." },
  { match: (t) => t === "highlight", hint: "Drag a wide translucent stroke to emphasise an area." },
  { match: (t) => t === "line", hint: "Click and drag from start to end." },
  { match: (t) => t === "arrow", hint: "Click and drag from the tail to the head of the arrow." },
  { match: (t) => t.startsWith("shape-"), hint: "Click and drag to draw the shape." },
  { match: (t) => t === "label", hint: "Click where the text should go, then edit it in the panel below." },
  { match: (t) => t === "measure", hint: "Drag between two points to measure the distance." },
  { match: (t) => t === "calibrate", hint: "Drag along something with a known length (a fence panel, a wall), then enter its real length." },
  { match: (t) => t.startsWith("zone-"), hint: "Click and drag to draw the zone." },
  { match: (t) => t.startsWith("route-"), hint: "Click to add points along the route. Double-click or press Finish to complete." },
  { match: (t) => t.startsWith("barrier-"), hint: "Click to add points along the line. Double-click or press Finish to complete." },
  { match: (t) => t.startsWith("icon-"), hint: "Click the plan to place the marker." },
  { match: (t) => t.startsWith("stencil-"), hint: "Click the plan to place the full RAMS control pack. Each item can be moved or edited afterwards." },
];

function toolHint(tool: Tool): string {
  return TOOL_HINTS.find((h) => h.match(tool))?.hint ?? "";
}

function toolLabel(tool: Tool): string {
  if (tool === "select") return "Select / move";
  if (tool === "pan") return "Pan";
  if (tool === "draw") return "Pen";
  if (tool === "highlight") return "Highlighter";
  if (tool === "line") return "Line";
  if (tool === "arrow") return "Arrow";
  if (tool === "label") return "Text";
  if (tool === "measure") return "Measure";
  if (tool === "calibrate") return "Set scale";
  if (tool.startsWith("shape-")) return `${tool.slice(6)[0].toUpperCase()}${tool.slice(7)} shape`;
  if (tool.startsWith("zone-")) return ZONE_STYLE[tool.slice(5) as ZoneType].label;
  if (tool.startsWith("route-")) return ROUTE_STYLE[tool.slice(6) as RouteType].label;
  if (tool.startsWith("barrier-")) return BARRIER_STYLE[tool.slice(8) as BarrierType].label;
  if (tool.startsWith("icon-")) return ICON_META[tool.slice(5) as IconType].label;
  if (tool.startsWith("stencil-")) return findCompositeStencil(tool.slice(8))?.label ?? "RAMS stencil";
  return tool;
}

const KEY_TOOL_MAP: Record<string, Tool> = {
  v: "select",
  h: "pan",
  p: "draw",
  g: "highlight",
  l: "line",
  a: "arrow",
  r: "shape-rect",
  o: "shape-ellipse",
  t: "label",
  m: "measure",
  b: "barrier-boundary",
};

/* ── Toolbar building blocks ─────────────────────────────────────────────── */

function toolButtonClass(active: boolean, danger = false) {
  return `inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md border text-[0.78rem] font-bold transition-colors focus:outline-none focus:ring-2 focus:ring-emerald/35 disabled:cursor-not-allowed disabled:opacity-40 ${
    active
      ? "border-emerald bg-emerald-tint text-emerald-deep"
      : danger
        ? "border-risk-veryhigh/35 bg-white text-risk-veryhigh hover:bg-[#fdecec]"
        : "border-line-2 bg-white text-muted hover:border-ink hover:text-ink"
  }`;
}

function Glyph({ d, extra }: { d: string; extra?: React.ReactNode }) {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d={d} />
      {extra}
    </svg>
  );
}

const GLYPHS = {
  pointer: <Glyph d="M5 3l10 10-5 1.5L8.5 20 5 3z" extra={<path d="M13 13l5 5" />} />,
  hand: <Glyph d="M8 12V5.5a1.5 1.5 0 0 1 3 0V11m0-5.5v-1a1.5 1.5 0 0 1 3 0V11m0-4.5a1.5 1.5 0 0 1 3 0V12" extra={<path d="M17 11.5c1.5.5 2.5 1.5 2 3l-1.3 4A4 4 0 0 1 13.8 21h-2.6a4 4 0 0 1-3.4-1.9L5 13.5c-.8-1.3.8-2.7 2-1.8l1 .8" />} />,
  undo: <Glyph d="M9 14 4 9l5-5" extra={<path d="M4 9h10a6 6 0 0 1 0 12h-1" />} />,
  redo: <Glyph d="m15 14 5-5-5-5" extra={<path d="M20 9H10a6 6 0 0 0 0 12h1" />} />,
  trash: <Glyph d="M3 6h18" extra={<><path d="M8 6V4h8v2" /><path d="M6 6l1 15h10l1-15" /><path d="M10 11v6M14 11v6" /></>} />,
  image: <Glyph d="m21 15-4-4-8 8" extra={<><rect x="3" y="4" width="18" height="16" rx="2" /><circle cx="8.5" cy="9" r="1.5" /><path d="m13 15-2-2-5 5" /></>} />,
  imagePlus: <Glyph d="M15 4H5a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-8" extra={<><path d="M19 2v6M16 5h6" /><circle cx="8.5" cy="10" r="1.5" /><path d="m21 16-4-4-8 8" /></>} />,
  pen: <Glyph d="M12 19c2-6 6-10 8-11l-4-4C15 6 11 10 5 12" extra={<path d="M5 12c-1.5.5-2 4-2 7 3 0 6.5-.5 7-2" />} />,
  highlighter: <Glyph d="m9 11-6 6v3h9l3-3" extra={<path d="m22 12-4.6 4.6a2 2 0 0 1-2.8 0l-5.2-5.2a2 2 0 0 1 0-2.8L14 4" />} />,
  line: <Glyph d="M5 19 19 5" />,
  arrow: <Glyph d="M5 19 19 5" extra={<path d="M11 5h8v8" />} />,
  square: <Glyph d="" extra={<rect x="4" y="4" width="16" height="16" rx="1" />} />,
  circle: <Glyph d="" extra={<circle cx="12" cy="12" r="9" />} />,
  triangle: <Glyph d="M12 4 21 20H3L12 4z" />,
  ruler: <Glyph d="M3 17 17 3l4 4L7 21l-4-4z" extra={<path d="m7 13 1.5 1.5M10 10l1.5 1.5M13 7l1.5 1.5" />} />,
  scale: <Glyph d="M4 20h16" extra={<><path d="M4 16v4M20 16v4" /><path d="M8 18v2M12 18v2M16 18v2" /></>} />,
  zoomIn: <Glyph d="m21 21-4.3-4.3" extra={<><circle cx="11" cy="11" r="7" /><path d="M11 8v6M8 11h6" /></>} />,
  zoomOut: <Glyph d="m21 21-4.3-4.3" extra={<><circle cx="11" cy="11" r="7" /><path d="M8 11h6" /></>} />,
  fit: <Glyph d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" />,
  key: <Glyph d="M4 6h16M4 12h16M4 18h10" extra={<circle cx="19" cy="18" r="1.4" />} />,
  layers: <Glyph d="m12 2 9 5-9 5-9-5 9-5z" extra={<><path d="m3 12 9 5 9-5" /><path d="m3 17 9 5 9-5" /></>} />,
  text: <span className="font-display text-sm font-black" aria-hidden>T</span>,
};

function ZoneGlyph({ zoneType }: { zoneType: ZoneType }) {
  const style = ZONE_STYLE[zoneType];
  return (
    <span
      aria-hidden
      className="h-4 w-5 rounded-sm border-2"
      style={{
        borderColor: style.stroke,
        background:
          zoneType === "exclusion"
            ? `repeating-linear-gradient(135deg, rgba(220,38,38,0.16) 0 3px, transparent 3px 6px)`
            : style.fill,
      }}
    />
  );
}

function RouteGlyph({ routeType }: { routeType: RouteType }) {
  const style = ROUTE_STYLE[routeType];
  return (
    <svg viewBox="0 0 28 16" className="h-4 w-6" aria-hidden>
      <path
        d="M3 11 C9 3 17 14 25 5"
        fill="none"
        stroke={style.stroke}
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeDasharray={style.dash ? "4 3" : undefined}
      />
      <path d="M22 3 26 5 23 8" fill="none" stroke={style.stroke} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function BarrierGlyph({ barrierType }: { barrierType: BarrierType }) {
  const s = BARRIER_STYLE[barrierType];
  return (
    <svg viewBox="0 0 28 16" className="h-4 w-6" aria-hidden>
      {s.deco === "double" ? (
        <>
          <line x1="3" y1="8" x2="25" y2="8" stroke={s.stroke} strokeWidth="7" strokeLinecap="round" />
          <line x1="3" y1="8" x2="25" y2="8" stroke="white" strokeWidth="4" strokeLinecap="round" />
        </>
      ) : (
        <line x1="3" y1="8" x2="25" y2="8" stroke={s.stroke} strokeWidth="2.4" strokeLinecap="round" strokeDasharray={s.dash ? "5 3" : undefined} />
      )}
      {s.deco === "posts" && [8, 14, 20].map((x) => <line key={x} x1={x} y1="4" x2={x} y2="12" stroke={s.stroke} strokeWidth="2" />)}
      {s.deco === "ticks" && [8, 14, 20].map((x) => <line key={x} x1={x} y1="3" x2={x} y2="8" stroke={s.stroke} strokeWidth="1.8" />)}
      {s.deco === "letter" && (
        <text x="14" y="11.5" fontSize="9" fontWeight="800" fill={s.stroke} textAnchor="middle" stroke="white" strokeWidth="2.5" paintOrder="stroke">
          {s.letter}
        </text>
      )}
    </svg>
  );
}

function ToolbarGroup({ label, children }: { label?: string; children: React.ReactNode }) {
  return (
    <div className="flex min-w-0 max-w-full flex-wrap items-center gap-1 rounded-lg bg-white/70 px-1.5 py-1">
      {label && <span className="shrink-0 px-0.5 text-[0.6rem] font-bold uppercase text-muted">{label}</span>}
      {children}
    </div>
  );
}

function ChevronGlyph({ open }: { open: boolean }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={`h-3 w-3 transition-transform ${open ? "rotate-180" : ""}`}
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

/** Category button with a flyout menu anchored below it. */
function ToolbarMenu({
  id,
  label,
  openMenu,
  setOpenMenu,
  active,
  icon,
  width = "w-64",
  iconOnly = false,
  title,
  children,
}: {
  id: string;
  label: string;
  openMenu: string | null;
  setOpenMenu: (id: string | null) => void;
  active: boolean;
  icon: React.ReactNode;
  width?: string;
  /** Hide the inline text label (icon + chevron only) to save toolbar width. */
  iconOnly?: boolean;
  /** Tooltip override; defaults to label. */
  title?: string;
  children: React.ReactNode;
}) {
  const open = openMenu === id;
  return (
    <div className="relative shrink-0">
      <button
        type="button"
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={iconOnly ? title ?? label : undefined}
        title={title ?? label}
        className={`inline-flex h-9 items-center gap-1.5 rounded-md border px-2 text-[0.78rem] font-bold transition-colors focus:outline-none focus:ring-2 focus:ring-emerald/35 ${
          active || open
            ? "border-emerald bg-emerald-tint text-emerald-deep"
            : "border-line-2 bg-white text-muted hover:border-ink hover:text-ink"
        }`}
        onClick={() => setOpenMenu(open ? null : id)}
      >
        {icon}
        {!iconOnly && <span className="hidden sm:inline">{label}</span>}
        <ChevronGlyph open={open} />
      </button>
      {open && (
        <div className={`absolute left-0 top-full z-30 mt-1 ${width} max-h-[24rem] overflow-y-auto rounded-lg border border-line bg-white p-1.5 shadow-lg`}>
          {children}
        </div>
      )}
    </div>
  );
}

function MenuItem({
  active = false,
  onClick,
  swatch,
  label,
  shortcut,
  disabled,
}: {
  active?: boolean;
  onClick: () => void;
  swatch: React.ReactNode;
  label: string;
  shortcut?: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      className={`flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-left text-[0.82rem] transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
        active ? "bg-emerald-tint font-semibold text-emerald-deep" : "text-body hover:bg-mist"
      }`}
      onClick={onClick}
    >
      <span className="flex w-6 shrink-0 justify-center">{swatch}</span>
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {shortcut && <span className="shrink-0 text-[0.68rem] font-bold uppercase text-muted">{shortcut}</span>}
    </button>
  );
}

function ToolButton({
  active = false,
  danger = false,
  label,
  shortcut,
  onClick,
  disabled,
  children,
}: {
  active?: boolean;
  danger?: boolean;
  label: string;
  shortcut?: string;
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  const title = shortcut ? `${label} (${shortcut.toUpperCase()})` : label;
  return (
    <button type="button" aria-label={label} title={title} className={toolButtonClass(active, danger)} onClick={onClick} disabled={disabled}>
      {children}
    </button>
  );
}

/* ── Recently-used markers ───────────────────────────────────────────────── */

function loadRecentIcons(): IconType[] {
  try {
    const raw = window.localStorage.getItem(RECENT_ICON_STORE);
    const parsed = raw ? (JSON.parse(raw) as string[]) : [];
    return parsed.filter((i): i is IconType => i in ICON_META);
  } catch {
    return [];
  }
}

function pushRecentIcon(icon: IconType, current: IconType[]): IconType[] {
  const next = [icon, ...current.filter((i) => i !== icon)].slice(0, 8);
  try {
    window.localStorage.setItem(RECENT_ICON_STORE, JSON.stringify(next));
  } catch {
    /* non-fatal */
  }
  return next;
}

/* ── Selection handles overlay ───────────────────────────────────────────── */

const BOX_HANDLES: { handle: BoxHandle; fx: number; fy: number; cursor: string }[] = [
  { handle: "nw", fx: 0, fy: 0, cursor: "nwse-resize" },
  { handle: "n", fx: 0.5, fy: 0, cursor: "ns-resize" },
  { handle: "ne", fx: 1, fy: 0, cursor: "nesw-resize" },
  { handle: "e", fx: 1, fy: 0.5, cursor: "ew-resize" },
  { handle: "se", fx: 1, fy: 1, cursor: "nwse-resize" },
  { handle: "s", fx: 0.5, fy: 1, cursor: "ns-resize" },
  { handle: "sw", fx: 0, fy: 1, cursor: "nesw-resize" },
  { handle: "w", fx: 0, fy: 0.5, cursor: "ew-resize" },
];

function HandleDot({
  x,
  y,
  zoom,
  cursor,
  onPointerDown,
  shape = "square",
}: {
  x: number;
  y: number;
  zoom: number;
  cursor: string;
  onPointerDown: (e: React.PointerEvent) => void;
  shape?: "square" | "round";
}) {
  const r = 5 / zoom;
  const common = {
    fill: "white",
    stroke: "#0f172a",
    strokeWidth: 1.4 / zoom,
    style: { cursor },
    onPointerDown,
  };
  return shape === "round" ? (
    <circle cx={x} cy={y} r={r} {...common} />
  ) : (
    <rect x={x - r} y={y - r} width={r * 2} height={r * 2} {...common} />
  );
}

/* ── Editor component ────────────────────────────────────────────────────── */

export function SitePlanEditor({
  value,
  onChange,
  background,
  onBackgroundChange,
  title = "Site plan sketch",
  hint = "Sketch the site: draw the routes and zones, place the key points. The sketch prints with the document and is saved on this device.",
  defaultPreset = "all",
}: {
  value: PlanElement[];
  onChange: (els: PlanElement[]) => void;
  background?: PlanBackground | null;
  onBackgroundChange?: (bg: PlanBackground | null) => void;
  title?: string;
  hint?: string;
  /** Plan preset pre-selected for this host tool (a PLAN_PRESETS id or "all"). Still switchable by the user. */
  defaultPreset?: string;
}) {
  const markerPrefix = useId().replace(/[:]/g, "");
  const svgRef = useRef<SVGSVGElement | null>(null);
  const [tool, setTool] = useState<Tool>("select");
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [annotColor, setAnnotColor] = useState<string>("#dc2626");

  const [draftRect, setDraftRect] = useState<{ x: number; y: number; w: number; h: number; zoneType?: ZoneType; shape?: ShapeType; color?: string } | null>(null);
  const [draftRoute, setDraftRoute] = useState<{ x: number; y: number }[]>([]);
  const [draftBarrier, setDraftBarrier] = useState<{ x: number; y: number }[]>([]);
  const [preset, setPreset] = useState(
    defaultPreset === "all" || PLAN_PRESETS.some((p) => p.id === defaultPreset) ? defaultPreset : "all",
  );
  const [draftLine, setDraftLine] = useState<{ x1: number; y1: number; x2: number; y2: number; purpose: "line" | "arrow" | "measure" | "calibrate" } | null>(null);
  const [draftDraw, setDraftDraw] = useState<{ points: { x: number; y: number }[]; highlight: boolean } | null>(null);
  const [pendingCalibration, setPendingCalibration] = useState<{ x1: number; y1: number; x2: number; y2: number } | null>(null);
  const [calibrationMetres, setCalibrationMetres] = useState("");

  const [history, setHistory] = useState<PlanElement[][]>([]);
  const [future, setFuture] = useState<PlanElement[][]>([]);
  const [confirmClear, setConfirmClear] = useState(false);
  const [savedAt, setSavedAt] = useState<Date | null>(null);

  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });

  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [markerSearch, setMarkerSearch] = useState("");
  const [stencilSearch, setStencilSearch] = useState("");
  const toolbarRef = useRef<HTMLDivElement | null>(null);

  // Close any open flyout when clicking outside the toolbar.
  useEffect(() => {
    const onDown = (e: PointerEvent) => {
      if (toolbarRef.current && !toolbarRef.current.contains(e.target as Node)) setOpenMenu(null);
    };
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, []);
  const [recentIcons, setRecentIcons] = useState<IconType[]>([]);

  const actionRef = useRef<PointerAction | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const bgInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => setRecentIcons(loadRecentIcons()), []);

  const selected = value.find((e) => e.id === selectedId) ?? null;
  const draftRouteType: RouteType | undefined = tool.startsWith("route-") ? (tool.slice(6) as RouteType) : undefined;
  const draftBarrierType: BarrierType | undefined = tool.startsWith("barrier-") ? (tool.slice(8) as BarrierType) : undefined;
  const activeStencil = tool.startsWith("stencil-") ? findCompositeStencil(tool.slice(8)) : null;
  const mpu = metresPerUnit(value);
  const calibration = findCalibration(value);

  // Active preset filter — "all" shows every tool.
  const activePreset = PLAN_PRESETS.find((p) => p.id === preset) ?? null;
  const presetLabel = activePreset?.label ?? "All layers";
  const presetZones = activePreset ? activePreset.zones : (Object.keys(ZONE_STYLE) as ZoneType[]);
  const presetRoutes = activePreset ? activePreset.routes : (Object.keys(ROUTE_STYLE) as RouteType[]);
  const presetBarriers = activePreset ? activePreset.barriers : (Object.keys(BARRIER_STYLE) as BarrierType[]);

  /* ── History ── */

  const markSaved = () => setSavedAt(new Date());

  const commit = (els: PlanElement[]) => {
    setHistory((h) => [...h.slice(-(HISTORY_CAP - 1)), value]);
    setFuture([]);
    onChange(els);
    markSaved();
  };

  const undo = () => {
    if (history.length === 0) return;
    const prev = history[history.length - 1];
    setHistory((h) => h.slice(0, -1));
    setFuture((f) => [...f, value]);
    onChange(prev);
    markSaved();
    setSelectedId(null);
  };

  const redo = () => {
    if (future.length === 0) return;
    const next = future[future.length - 1];
    setFuture((f) => f.slice(0, -1));
    setHistory((h) => [...h.slice(-(HISTORY_CAP - 1)), value]);
    onChange(next);
    markSaved();
    setSelectedId(null);
  };

  /* ── Zoom & pan ── */

  const viewW = W / zoom;
  const viewH = H / zoom;

  const clampPan = (p: { x: number; y: number }, z: number) => ({
    x: Math.max(0, Math.min(W - W / z, p.x)),
    y: Math.max(0, Math.min(H - H / z, p.y)),
  });

  const setZoomAt = (nextZoom: number, focus?: { x: number; y: number }) => {
    const z = Math.max(1, Math.min(MAX_ZOOM, nextZoom));
    const f = focus ?? { x: pan.x + viewW / 2, y: pan.y + viewH / 2 };
    // Keep the focus point stationary in the viewport.
    const fx = (f.x - pan.x) / viewW;
    const fy = (f.y - pan.y) / viewH;
    setPan(clampPan({ x: f.x - fx * (W / z), y: f.y - fy * (H / z) }, z));
    setZoom(z);
  };

  const zoomIn = (focus?: { x: number; y: number }) => setZoomAt(zoom * 1.4, focus);
  const zoomOut = (focus?: { x: number; y: number }) => setZoomAt(zoom / 1.4, focus);
  const zoomReset = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };

  // Ctrl/Cmd + wheel zoom, anchored at the cursor. Needs a non-passive listener.
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      const rect = svg.getBoundingClientRect();
      const focus = {
        x: pan.x + ((e.clientX - rect.left) / rect.width) * viewW,
        y: pan.y + ((e.clientY - rect.top) / rect.height) * viewH,
      };
      setZoomAt(e.deltaY < 0 ? zoom * 1.25 : zoom / 1.25, focus);
    };
    svg.addEventListener("wheel", onWheel, { passive: false });
    return () => svg.removeEventListener("wheel", onWheel);
  });

  /** Converts a pointer event to canvas coordinates (zoom/pan aware). */
  const toCanvas = (e: React.PointerEvent): { x: number; y: number } => {
    const rect = svgRef.current!.getBoundingClientRect();
    return {
      x: Math.max(0, Math.min(W, pan.x + ((e.clientX - rect.left) / rect.width) * viewW)),
      y: Math.max(0, Math.min(H, pan.y + ((e.clientY - rect.top) / rect.height) * viewH)),
    };
  };

  /* ── Element mutation helpers ── */

  /** Property edits (text, colour, sliders) save live without flooding history. */
  const updateSelected = (patch: Partial<PlanElement>) => {
    if (selectedId === null) return;
    onChange(value.map((el) => (el.id === selectedId ? ({ ...el, ...patch } as PlanElement) : el)));
    markSaved();
  };

  /** Live (non-committing) update used mid-drag; history snapshot lands on pointer-up. */
  const liveUpdate = (id: number, make: (origin: PlanElement) => PlanElement) => {
    onChange(value.map((el) => (el.id === id ? make(el) : el)));
  };

  const removeSelected = () => {
    if (selectedId === null) return;
    commit(value.filter((e) => e.id !== selectedId));
    setSelectedId(null);
  };

  const reorderSelected = (dir: "front" | "back") => {
    if (selectedId === null) return;
    const el = value.find((e) => e.id === selectedId);
    if (!el) return;
    const rest = value.filter((e) => e.id !== selectedId);
    commit(dir === "front" ? [...rest, el] : [el, ...rest]);
  };

  const startHandle = (e: React.PointerEvent, action: PointerAction) => {
    e.stopPropagation();
    // Capture on the persistent <svg> root — capturing on the handle itself
    // breaks the gesture when the handle re-renders mid-drag (Chrome fires
    // pointercancel when a captured child is touched by a React commit).
    svgRef.current?.setPointerCapture(e.pointerId);
    actionRef.current = action;
  };

  /* ── Pointer handlers ── */

  const onPointerDown = (e: React.PointerEvent) => {
    const p = toCanvas(e);
    // Capture on the svg root, never on child elements — children re-render
    // during drags and a re-rendered capture target kills the gesture.
    svgRef.current?.setPointerCapture(e.pointerId);

    if (tool === "pan") {
      actionRef.current = { mode: "pan", startClient: { x: e.clientX, y: e.clientY }, originPan: pan };
      return;
    }
    if (tool.startsWith("stencil-")) {
      const stencil = findCompositeStencil(tool.slice(8));
      if (!stencil) {
        setTool("select");
        return;
      }
      const els = instantiateCompositeStencil(stencil, p);
      commit([...value, ...els]);
      setSelectedId(els[0]?.id ?? null);
      setTool("select");
      return;
    }
    if (tool === "select") {
      const hit = hitTest(value, p);
      setSelectedId(hit?.id ?? null);
      if (hit) actionRef.current = { mode: "move", id: hit.id, start: p, origin: hit };
      return;
    }
    if (tool.startsWith("zone-")) {
      setDraftRect({ x: p.x, y: p.y, w: 0, h: 0, zoneType: tool.slice(5) as ZoneType });
      return;
    }
    if (tool.startsWith("shape-")) {
      setDraftRect({ x: p.x, y: p.y, w: 0, h: 0, shape: tool.slice(6) as ShapeType, color: annotColor });
      return;
    }
    if (tool === "line" || tool === "arrow" || tool === "measure" || tool === "calibrate") {
      setDraftLine({ x1: p.x, y1: p.y, x2: p.x, y2: p.y, purpose: tool });
      return;
    }
    if (tool === "draw" || tool === "highlight") {
      setDraftDraw({ points: [p], highlight: tool === "highlight" });
      return;
    }
    if (tool.startsWith("route-")) {
      setDraftRoute((pts) => [...pts, p]);
      return;
    }
    if (tool.startsWith("barrier-")) {
      setDraftBarrier((pts) => [...pts, p]);
      return;
    }
    if (tool === "label") {
      const el: LabelEl = { id: allocElId(), kind: "label", x: p.x, y: p.y, text: "Label" };
      commit([...value, el]);
      setSelectedId(el.id);
      setTool("select");
      return;
    }
    if (tool.startsWith("icon-")) {
      const icon = tool.slice(5) as IconType;
      const el: PlanElement = { id: allocElId(), kind: "icon", icon, x: p.x, y: p.y, radius: icon === "crane" ? 90 : undefined };
      commit([...value, el]);
      setRecentIcons((r) => pushRecentIcon(icon, r));
      setSelectedId(el.id);
      setTool("select");
      return;
    }
  };

  const applyResize = (origin: PlanElement & { x: number; y: number; w: number; h: number; rotation?: number }, handle: BoxHandle, p: { x: number; y: number }, start: { x: number; y: number }) => {
    // Work in the element's local (un-rotated) space around its original centre.
    const cx = origin.x + origin.w / 2;
    const cy = origin.y + origin.h / 2;
    const lp = toLocal(p, cx, cy, origin.rotation);
    const ls = toLocal(start, cx, cy, origin.rotation);
    const dx = lp.x - ls.x;
    const dy = lp.y - ls.y;
    let { x, y, w, h } = origin;
    if (handle.includes("w")) {
      x = Math.min(origin.x + dx, origin.x + origin.w - MIN_SIZE);
      w = origin.w - (x - origin.x);
    }
    if (handle.includes("e")) w = Math.max(MIN_SIZE, origin.w + dx);
    if (handle.includes("n")) {
      y = Math.min(origin.y + dy, origin.y + origin.h - MIN_SIZE);
      h = origin.h - (y - origin.y);
    }
    if (handle.includes("s")) h = Math.max(MIN_SIZE, origin.h + dy);
    return { x, y, w, h };
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const action = actionRef.current;

    if (action?.mode === "pan") {
      const rect = svgRef.current!.getBoundingClientRect();
      const dx = ((e.clientX - action.startClient.x) / rect.width) * viewW;
      const dy = ((e.clientY - action.startClient.y) / rect.height) * viewH;
      setPan(clampPan({ x: action.originPan.x - dx, y: action.originPan.y - dy }, zoom));
      return;
    }

    const p = toCanvas(e);

    if (draftRect) {
      setDraftRect((d) => (d ? { ...d, w: p.x - d.x, h: p.y - d.y } : d));
      return;
    }
    if (draftLine) {
      setDraftLine((d) => (d ? { ...d, x2: p.x, y2: p.y } : d));
      return;
    }
    if (draftDraw) {
      setDraftDraw((d) => {
        if (!d) return d;
        const last = d.points[d.points.length - 1];
        if (Math.hypot(p.x - last.x, p.y - last.y) < 2) return d;
        return { ...d, points: [...d.points, p] };
      });
      return;
    }
    if (!action) return;

    if (action.mode === "move") {
      const dx = p.x - action.start.x;
      const dy = p.y - action.start.y;
      liveUpdate(action.id, () => moveElement(action.origin, dx, dy));
      return;
    }
    if (action.mode === "resize") {
      const origin = action.origin;
      if (origin.kind === "zone" || origin.kind === "shape" || origin.kind === "image") {
        const box = applyResize(origin, action.handle, p, action.start);
        liveUpdate(action.id, (el) => ({ ...el, ...box }) as PlanElement);
      }
      return;
    }
    if (action.mode === "rotate") {
      const origin = action.origin;
      if (origin.kind === "zone" || origin.kind === "shape" || origin.kind === "image") {
        const cx = origin.x + origin.w / 2;
        const cy = origin.y + origin.h / 2;
        let deg = (Math.atan2(p.y - cy, p.x - cx) * 180) / Math.PI + 90;
        deg = ((deg % 360) + 360) % 360;
        const snap = Math.round(deg / 15) * 15;
        if (Math.abs(deg - snap) <= 3) deg = snap % 360;
        liveUpdate(action.id, (el) => ({ ...el, rotation: Math.round(deg) }) as PlanElement);
      }
      return;
    }
    if (action.mode === "endpoint") {
      liveUpdate(action.id, (el) => {
        if (el.kind !== "line" && el.kind !== "measure" && el.kind !== "calibration") return el;
        return action.which === 1 ? { ...el, x1: p.x, y1: p.y } : { ...el, x2: p.x, y2: p.y };
      });
      return;
    }
    if (action.mode === "vertex") {
      liveUpdate(action.id, (el) =>
        el.kind === "route" || el.kind === "barrier"
          ? { ...el, points: el.points.map((pt, i) => (i === action.index ? p : pt)) }
          : el,
      );
      return;
    }
    if (action.mode === "iconsize") {
      const origin = action.origin;
      if (origin.kind === "icon") {
        const size = Math.max(0.6, Math.min(2.4, Math.hypot(p.x - origin.x, p.y - origin.y) / 18));
        liveUpdate(action.id, (el) => ({ ...el, size: Math.round(size * 20) / 20 }) as PlanElement);
      }
      return;
    }
    if (action.mode === "radius") {
      const origin = action.origin;
      if (origin.kind === "icon") {
        const radius = Math.max(20, Math.min(420, Math.round(Math.hypot(p.x - origin.x, p.y - origin.y))));
        liveUpdate(action.id, (el) => ({ ...el, radius }) as PlanElement);
      }
      return;
    }
  };

  const onPointerUp = () => {
    if (draftRect) {
      const x = Math.min(draftRect.x, draftRect.x + draftRect.w);
      const y = Math.min(draftRect.y, draftRect.y + draftRect.h);
      const w = Math.abs(draftRect.w);
      const h = Math.abs(draftRect.h);
      if (w > 15 && h > 15) {
        const el: PlanElement = draftRect.zoneType
          ? { id: allocElId(), kind: "zone", zoneType: draftRect.zoneType, x, y, w, h, label: "" }
          : { id: allocElId(), kind: "shape", shape: draftRect.shape!, x, y, w, h, color: annotColor };
        commit([...value, el]);
        setSelectedId(el.id);
        setTool("select");
      }
      setDraftRect(null);
    }
    if (draftLine) {
      const { x1, y1, x2, y2, purpose } = draftLine;
      if (Math.hypot(x2 - x1, y2 - y1) > 12) {
        if (purpose === "calibrate") {
          setPendingCalibration({ x1, y1, x2, y2 });
          setCalibrationMetres("");
        } else if (purpose === "measure") {
          const el: MeasureEl = { id: allocElId(), kind: "measure", x1, y1, x2, y2 };
          commit([...value, el]);
          setSelectedId(el.id);
          setTool("select");
        } else {
          const el: LineEl = { id: allocElId(), kind: "line", x1, y1, x2, y2, color: annotColor, width: 3, arrow: purpose === "arrow" };
          commit([...value, el]);
          setSelectedId(el.id);
          setTool("select");
        }
      }
      setDraftLine(null);
    }
    if (draftDraw) {
      if (draftDraw.points.length >= 2) {
        const el: PlanElement = {
          id: allocElId(),
          kind: "draw",
          points: draftDraw.points,
          color: annotColor,
          width: draftDraw.highlight ? 14 : 3,
          highlight: draftDraw.highlight || undefined,
        };
        commit([...value, el]);
        setSelectedId(el.id);
      }
      setDraftDraw(null);
    }
    const action = actionRef.current;
    if (action && action.mode !== "pan") {
      // Live edits mutated state already; snapshot the pre-edit state into
      // history — but only if something actually changed (a plain click on an
      // element shouldn't burn a history step or clear the redo stack).
      const origin = action.origin;
      const current = value.find((el) => el.id === origin.id);
      if (current && current !== origin) {
        setHistory((h) => [...h.slice(-(HISTORY_CAP - 1)), value.map((el) => (el.id === origin.id ? origin : el))]);
        setFuture([]);
        markSaved();
      }
    }
    actionRef.current = null;
  };

  /** Drops near-duplicate trailing points (double-click to finish adds two). */
  const dedupePoints = (pts: { x: number; y: number }[]) =>
    pts.filter((p, i, arr) => i === 0 || Math.hypot(p.x - arr[i - 1].x, p.y - arr[i - 1].y) >= 3);

  const finishRoute = () => {
    const points = dedupePoints(draftRoute);
    if (points.length >= 2 && draftRouteType) {
      const el: PlanElement = { id: allocElId(), kind: "route", routeType: draftRouteType, points };
      commit([...value, el]);
      setSelectedId(el.id);
    }
    setDraftRoute([]);
    setTool("select");
  };

  const finishBarrier = () => {
    const points = dedupePoints(draftBarrier);
    if (points.length >= 2 && draftBarrierType) {
      const el: PlanElement = { id: allocElId(), kind: "barrier", barrierType: draftBarrierType, points };
      commit([...value, el]);
      setSelectedId(el.id);
    }
    setDraftBarrier([]);
    setTool("select");
  };

  const cancelDrafts = () => {
    setDraftRect(null);
    setDraftLine(null);
    setDraftDraw(null);
    setDraftRoute([]);
    setDraftBarrier([]);
    setPendingCalibration(null);
    setSelectedId(null);
    setOpenMenu(null);
    setTool("select");
  };

  const confirmCalibration = () => {
    if (!pendingCalibration) return;
    const metres = parseFloat(calibrationMetres);
    if (!Number.isFinite(metres) || metres <= 0) return;
    const el: CalibrationEl = { id: allocElId(), kind: "calibration", ...pendingCalibration, metres };
    // Single scale reference per sketch — replace any existing one.
    commit([...value.filter((e) => e.kind !== "calibration"), el]);
    setPendingCalibration(null);
    setTool("select");
  };

  const addLegend = () => {
    if (value.some((e) => e.kind === "legend")) return;
    const el: PlanElement = { id: allocElId(), kind: "legend", x: W - LEGEND_WIDTH - 14, y: 14 };
    commit([...value, el]);
    setSelectedId(el.id);
    setTool("select");
  };

  const insertImage = async (file: File) => {
    const el = await imageFileToElement(file);
    if (!el) return;
    commit([...value, el]);
    setSelectedId(el.id);
    setTool("select");
  };

  /* ── Keyboard shortcuts ── */

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.tagName === "SELECT" || target.isContentEditable)) return;
      if (e.ctrlKey || e.metaKey) {
        if (e.key.toLowerCase() === "z" && !e.shiftKey) {
          e.preventDefault();
          undo();
        } else if (e.key.toLowerCase() === "y" || (e.key.toLowerCase() === "z" && e.shiftKey)) {
          e.preventDefault();
          redo();
        }
        return;
      }
      if (e.key === "Escape") {
        cancelDrafts();
        return;
      }
      if (e.key === "Delete" || e.key === "Backspace") {
        if (selectedId !== null) {
          e.preventDefault();
          removeSelected();
        }
        return;
      }
      if (e.key === "+" || e.key === "=") return zoomIn();
      if (e.key === "-") return zoomOut();
      if (e.key === "0") return zoomReset();
      const mapped = KEY_TOOL_MAP[e.key.toLowerCase()];
      if (mapped) {
        setTool(mapped);
        setDraftRoute([]);
        setDraftBarrier([]);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  /* ── Stencil and marker palettes ── */

  const filteredStencilGroups = useMemo(() => {
    const q = stencilSearch.trim().toLowerCase();
    return COMPOSITE_STENCIL_GROUPS.map((group) => ({
      ...group,
      stencils: group.stencils.filter((stencil) => {
        if (!q) return true;
        return [stencil.id, stencil.label, stencil.category, stencil.summary, ...stencil.tags].some((part) =>
          part.toLowerCase().includes(q),
        );
      }),
    })).filter((group) => group.stencils.length > 0);
  }, [stencilSearch]);

  const filteredCategories = useMemo(() => {
    const q = markerSearch.trim().toLowerCase();
    const presetIcons = activePreset ? new Set<IconType>(activePreset.icons) : null;
    return ICON_CATEGORIES.map((c) => ({
      ...c,
      icons: c.icons.filter(
        (i) =>
          (!presetIcons || presetIcons.has(i)) &&
          (!q || ICON_META[i].label.toLowerCase().includes(q) || i.includes(q)),
      ),
    })).filter((c) => c.icons.length > 0);
  }, [markerSearch, activePreset]);

  const pickIcon = (icon: IconType) => {
    setTool(`icon-${icon}`);
    setSelectedId(null);
    setDraftRoute([]);
    setDraftBarrier([]);
    setOpenMenu(null);
  };

  const pickStencil = (stencil: CompositeStencil) => {
    setTool(`stencil-${stencil.id}`);
    setSelectedId(null);
    setDraftRect(null);
    setDraftLine(null);
    setDraftDraw(null);
    setDraftRoute([]);
    setDraftBarrier([]);
    setPendingCalibration(null);
    setOpenMenu(null);
  };

  /** Selects a tool from a flyout menu and closes it. */
  const pickTool = (t: Tool) => {
    setTool(t);
    setSelectedId(null);
    setDraftRoute([]);
    setDraftBarrier([]);
    setOpenMenu(null);
  };

  /* ── Selection overlay (inside the SVG) ── */

  const selectionOverlay = (() => {
    if (!selected || tool !== "select") return null;
    const hs = 5 / zoom;

    if (selected.kind === "zone" || selected.kind === "shape" || selected.kind === "image") {
      const { x, y, w, h, rotation } = selected;
      const cx = x + w / 2;
      const cy = y + h / 2;
      return (
        <g transform={rotation ? `rotate(${rotation} ${cx} ${cy})` : undefined}>
          <rect x={x} y={y} width={w} height={h} fill="none" stroke="#0f172a" strokeWidth={1 / zoom} strokeDasharray={`${4 / zoom} ${3 / zoom}`} />
          {BOX_HANDLES.map(({ handle, fx, fy, cursor }) => (
            <HandleDot
              key={handle}
              x={x + fx * w}
              y={y + fy * h}
              zoom={zoom}
              cursor={cursor}
              onPointerDown={(e) => startHandle(e, { mode: "resize", id: selected.id, handle, start: toCanvas(e), origin: selected })}
            />
          ))}
          <line x1={cx} y1={y} x2={cx} y2={y - 20 / zoom} stroke="#0f172a" strokeWidth={1 / zoom} />
          <HandleDot
            x={cx}
            y={y - 20 / zoom}
            zoom={zoom}
            cursor="grab"
            shape="round"
            onPointerDown={(e) => startHandle(e, { mode: "rotate", id: selected.id, origin: selected })}
          />
        </g>
      );
    }
    if (selected.kind === "line" || selected.kind === "measure" || selected.kind === "calibration") {
      return (
        <g>
          {[1, 2].map((which) => (
            <HandleDot
              key={which}
              x={which === 1 ? selected.x1 : selected.x2}
              y={which === 1 ? selected.y1 : selected.y2}
              zoom={zoom}
              cursor="move"
              shape="round"
              onPointerDown={(e) => startHandle(e, { mode: "endpoint", id: selected.id, which: which as 1 | 2, origin: selected })}
            />
          ))}
        </g>
      );
    }
    if (selected.kind === "route" || selected.kind === "barrier") {
      return (
        <g>
          {selected.points.map((pt, i) => (
            <HandleDot
              key={i}
              x={pt.x}
              y={pt.y}
              zoom={zoom}
              cursor="move"
              shape="round"
              onPointerDown={(e) => startHandle(e, { mode: "vertex", id: selected.id, index: i, origin: selected })}
            />
          ))}
        </g>
      );
    }
    if (selected.kind === "icon") {
      const r = 13 * (selected.size ?? 1);
      return (
        <g>
          <HandleDot
            x={selected.x + r + 4 / zoom}
            y={selected.y + r + 4 / zoom}
            zoom={zoom}
            cursor="nwse-resize"
            onPointerDown={(e) => startHandle(e, { mode: "iconsize", id: selected.id, origin: selected })}
          />
          {selected.icon === "crane" && selected.radius && (
            <HandleDot
              x={selected.x + selected.radius}
              y={selected.y}
              zoom={zoom}
              cursor="ew-resize"
              shape="round"
              onPointerDown={(e) => startHandle(e, { mode: "radius", id: selected.id, origin: selected })}
            />
          )}
        </g>
      );
    }
    return null;
  })();

  /* ── Selection panel metadata ── */

  const selectedLabel = selected
    ? selected.kind === "zone"
      ? ZONE_STYLE[selected.zoneType].label
      : selected.kind === "route"
        ? ROUTE_STYLE[selected.routeType].label
        : selected.kind === "barrier"
          ? BARRIER_STYLE[selected.barrierType].label
          : selected.kind === "icon"
          ? ICON_META[selected.icon].label
          : selected.kind === "label"
            ? "Text label"
            : selected.kind === "draw"
              ? selected.highlight
                ? "Highlight"
                : "Freehand mark"
              : selected.kind === "line"
                ? selected.arrow
                  ? "Arrow"
                  : "Line"
                : selected.kind === "shape"
                  ? `${selected.shape[0].toUpperCase()}${selected.shape.slice(1)} shape`
                  : selected.kind === "image"
                    ? `Image — ${selected.name}`
                    : selected.kind === "measure"
                      ? "Measurement"
                      : selected.kind === "calibration"
                        ? "Scale reference"
                        : "Legend"
    : null;

  const colorEditable =
    selected &&
    (selected.kind === "zone" ||
      selected.kind === "route" ||
      selected.kind === "icon" ||
      selected.kind === "label" ||
      selected.kind === "draw" ||
      selected.kind === "line" ||
      selected.kind === "shape");

  const zones = presetZones;
  const routes = presetRoutes;
  const barriers = presetBarriers;

  return (
    <div className="card min-w-0 shadow-soft p-6 print-hide">
      <h2 className="text-xl">{title}</h2>
      <p className="mt-1 text-sm text-muted">{hint}</p>

      {/* Toolbar — compact category buttons with flyout menus */}
      <div ref={toolbarRef} className="mt-4 min-w-0 rounded-lg border border-line bg-mist/65">
        <div className="flex min-w-0 flex-wrap items-center gap-1.5 p-2" role="toolbar" aria-label="Site plan drawing toolbar">
          {/* Plan preset — layer filter for common RAMS plan types; usually set from the host tool */}
          <ToolbarGroup>
            <ToolbarMenu
              id="preset"
              label={presetLabel}
              title={`Plan preset: ${presetLabel}`}
              openMenu={openMenu}
              setOpenMenu={setOpenMenu}
              active={preset !== "all"}
              icon={GLYPHS.layers}
              width="w-56"
              iconOnly
            >
              {[{ id: "all", label: "All layers" }, ...PLAN_PRESETS].map((p) => (
                <MenuItem
                  key={p.id}
                  active={preset === p.id}
                  onClick={() => {
                    setPreset(p.id);
                    setMarkerSearch("");
                    setStencilSearch("");
                    setOpenMenu(null);
                  }}
                  swatch={preset === p.id ? GLYPHS.layers : <span className="h-4 w-4" />}
                  label={p.label}
                />
              ))}
            </ToolbarMenu>
          </ToolbarGroup>

          <ToolbarGroup>
            <ToolButton active={tool === "select"} label="Select / move" shortcut="v" onClick={() => { setTool("select"); setDraftRoute([]); }}>
              {GLYPHS.pointer}
            </ToolButton>
            <ToolButton active={tool === "pan"} label="Pan" shortcut="h" onClick={() => setTool("pan")}>
              {GLYPHS.hand}
            </ToolButton>
            <ToolButton label="Undo" shortcut="ctrl+z" onClick={undo} disabled={history.length === 0}>
              {GLYPHS.undo}
            </ToolButton>
            <ToolButton label="Redo" shortcut="ctrl+y" onClick={redo} disabled={future.length === 0}>
              {GLYPHS.redo}
            </ToolButton>
            <ToolButton
              active={confirmClear}
              danger
              label={confirmClear ? "Click again to clear sketch" : "Clear sketch"}
              onClick={() => {
                if (confirmClear) {
                  commit([]);
                  setSelectedId(null);
                  setConfirmClear(false);
                } else {
                  setConfirmClear(true);
                  setTimeout(() => setConfirmClear(false), 2500);
                }
              }}
            >
              {GLYPHS.trash}
            </ToolButton>
          </ToolbarGroup>

          <ToolbarGroup>
            <ToolButton label="Zoom out" shortcut="-" onClick={() => zoomOut()} disabled={zoom <= 1}>
              {GLYPHS.zoomOut}
            </ToolButton>
            <span className="min-w-[3rem] text-center text-[0.75rem] font-bold text-ink">{Math.round(zoom * 100)}%</span>
            <ToolButton label="Zoom in" shortcut="+" onClick={() => zoomIn()} disabled={zoom >= MAX_ZOOM}>
              {GLYPHS.zoomIn}
            </ToolButton>
            <ToolButton label="Reset zoom" shortcut="0" onClick={zoomReset} disabled={zoom === 1}>
              {GLYPHS.fit}
            </ToolButton>
          </ToolbarGroup>

          <ToolbarMenu
            id="draw"
            label="Draw"
            openMenu={openMenu}
            setOpenMenu={setOpenMenu}
            active={["draw", "highlight", "line", "arrow", "shape-rect", "shape-ellipse", "shape-triangle", "label"].includes(tool)}
            icon={tool === "highlight" ? GLYPHS.highlighter : tool === "line" ? GLYPHS.line : tool === "arrow" ? GLYPHS.arrow : tool === "shape-rect" ? GLYPHS.square : tool === "shape-ellipse" ? GLYPHS.circle : tool === "shape-triangle" ? GLYPHS.triangle : tool === "label" ? GLYPHS.text : GLYPHS.pen}
            width="w-52"
          >
            <MenuItem active={tool === "draw"} onClick={() => pickTool("draw")} swatch={GLYPHS.pen} label="Pen" shortcut="P" />
            <MenuItem active={tool === "highlight"} onClick={() => pickTool("highlight")} swatch={GLYPHS.highlighter} label="Highlighter" shortcut="G" />
            <MenuItem active={tool === "line"} onClick={() => pickTool("line")} swatch={GLYPHS.line} label="Line" shortcut="L" />
            <MenuItem active={tool === "arrow"} onClick={() => pickTool("arrow")} swatch={GLYPHS.arrow} label="Arrow" shortcut="A" />
            <MenuItem active={tool === "shape-rect"} onClick={() => pickTool("shape-rect")} swatch={GLYPHS.square} label="Rectangle" shortcut="R" />
            <MenuItem active={tool === "shape-ellipse"} onClick={() => pickTool("shape-ellipse")} swatch={GLYPHS.circle} label="Ellipse" shortcut="O" />
            <MenuItem active={tool === "shape-triangle"} onClick={() => pickTool("shape-triangle")} swatch={GLYPHS.triangle} label="Triangle" />
            <MenuItem active={tool === "label"} onClick={() => pickTool("label")} swatch={GLYPHS.text} label="Text" shortcut="T" />
          </ToolbarMenu>

          <ToolbarMenu
            id="zones"
            label="Zones"
            openMenu={openMenu}
            setOpenMenu={setOpenMenu}
            active={tool.startsWith("zone-")}
            icon={<ZoneGlyph zoneType={tool.startsWith("zone-") ? (tool.slice(5) as ZoneType) : "exclusion"} />}
          >
            {zones.map((zt) => (
              <MenuItem
                key={zt}
                active={tool === `zone-${zt}`}
                onClick={() => pickTool(`zone-${zt}`)}
                swatch={<ZoneGlyph zoneType={zt} />}
                label={ZONE_STYLE[zt].label}
              />
            ))}
          </ToolbarMenu>

          <ToolbarMenu
            id="routes"
            label="Routes"
            openMenu={openMenu}
            setOpenMenu={setOpenMenu}
            active={tool.startsWith("route-")}
            icon={<RouteGlyph routeType={tool.startsWith("route-") ? (tool.slice(6) as RouteType) : "vehicle"} />}
          >
            {routes.map((rt) => (
              <MenuItem
                key={rt}
                active={tool === `route-${rt}`}
                onClick={() => pickTool(`route-${rt}`)}
                swatch={<RouteGlyph routeType={rt} />}
                label={ROUTE_STYLE[rt].label}
              />
            ))}
          </ToolbarMenu>

          <ToolbarMenu
            id="lines"
            label="Lines"
            openMenu={openMenu}
            setOpenMenu={setOpenMenu}
            active={tool.startsWith("barrier-")}
            icon={<BarrierGlyph barrierType={tool.startsWith("barrier-") ? (tool.slice(8) as BarrierType) : "boundary"} />}
          >
            {barriers.map((bt) => (
              <MenuItem
                key={bt}
                active={tool === `barrier-${bt}`}
                onClick={() => pickTool(`barrier-${bt}`)}
                swatch={<BarrierGlyph barrierType={bt} />}
                label={BARRIER_STYLE[bt].label}
              />
            ))}
          </ToolbarMenu>

          <ToolbarMenu
            id="scale"
            label="Scale"
            openMenu={openMenu}
            setOpenMenu={setOpenMenu}
            active={tool === "measure" || tool === "calibrate"}
            icon={GLYPHS.ruler}
            width="w-64"
          >
            <MenuItem active={tool === "measure"} onClick={() => pickTool("measure")} swatch={GLYPHS.ruler} label="Measure distance" shortcut="M" />
            <MenuItem
              active={tool === "calibrate"}
              onClick={() => pickTool("calibrate")}
              swatch={GLYPHS.scale}
              label={calibration ? "Redraw scale reference" : "Set scale from a known length"}
            />
          </ToolbarMenu>

          <ToolbarMenu
            id="markers"
            label="Markers"
            openMenu={openMenu}
            setOpenMenu={setOpenMenu}
            active={tool.startsWith("icon-")}
            icon={tool.startsWith("icon-") ? <IconSwatch icon={tool.slice(5) as IconType} /> : <IconSwatch icon="fire" />}
            width="w-[21rem]"
          >
            <div className="p-1">
              <input
                type="search"
                value={markerSearch}
                onChange={(e) => setMarkerSearch(e.target.value)}
                placeholder="Search markers — fire, crane, asbestos…"
                className="w-full rounded-md border border-line-2 px-3 py-1.5 text-sm focus:border-emerald focus:outline-none"
              />
              {recentIcons.length > 0 && !markerSearch.trim() && (
                <div className="mt-2.5">
                  <div className="px-1 text-[0.65rem] font-bold uppercase tracking-wide text-muted">Recently used</div>
                  <div className="mt-0.5">
                    {recentIcons.slice(0, 4).map((ic) => (
                      <MenuItem key={ic} active={tool === `icon-${ic}`} onClick={() => pickIcon(ic)} swatch={<IconSwatch icon={ic} />} label={ICON_META[ic].label} />
                    ))}
                  </div>
                </div>
              )}
              {filteredCategories.map((cat) => (
                <div key={cat.heading} className="mt-2.5">
                  <div className="px-1 text-[0.65rem] font-bold uppercase tracking-wide text-muted">{cat.heading}</div>
                  <div className="mt-0.5">
                    {cat.icons.map((ic) => (
                      <MenuItem key={ic} active={tool === `icon-${ic}`} onClick={() => pickIcon(ic)} swatch={<IconSwatch icon={ic} />} label={ICON_META[ic].label} />
                    ))}
                  </div>
                </div>
              ))}
              {filteredCategories.length === 0 && <p className="mt-3 px-1 text-sm text-muted">No markers match “{markerSearch}”.</p>}
            </div>
          </ToolbarMenu>

          <ToolbarMenu
            id="stencils"
            label="Stencils"
            openMenu={openMenu}
            setOpenMenu={setOpenMenu}
            active={!!activeStencil}
            icon={GLYPHS.key}
            width="w-[24rem]"
          >
            <div className="p-1">
              <input
                type="search"
                value={stencilSearch}
                onChange={(e) => setStencilSearch(e.target.value)}
                placeholder="Search stencils — hot works, crane, excavation…"
                className="w-full rounded-md border border-line-2 px-3 py-1.5 text-sm focus:border-emerald focus:outline-none"
              />
              <p className="mt-1.5 px-1 text-[0.72rem] text-muted">Place a pack, then edit its zones, routes, labels and markers.</p>
              {filteredStencilGroups.map((group) => (
                <div key={group.heading} className="mt-2.5">
                  <div className="px-1 text-[0.65rem] font-bold uppercase tracking-wide text-muted">{group.heading}</div>
                  <div className="mt-1 space-y-1">
                    {group.stencils.map((stencil) => (
                      <button
                        key={stencil.id}
                        type="button"
                        aria-label={`Place ${stencil.label}`}
                        className={`w-full rounded-md border px-2.5 py-2 text-left transition-colors ${
                          activeStencil?.id === stencil.id
                            ? "border-emerald bg-emerald-tint text-emerald-deep"
                            : "border-line-2 bg-white text-body hover:border-ink"
                        }`}
                        onClick={() => pickStencil(stencil)}
                      >
                        <span className="block text-[0.82rem] font-bold leading-snug">{stencil.label}</span>
                        <span className="mt-0.5 block text-[0.72rem] leading-snug text-muted">{stencil.summary}</span>
                        <span className="mt-1 block text-[0.65rem] font-bold uppercase tracking-wide text-muted">
                          {stencil.elements.length} editable items
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
              {filteredStencilGroups.length === 0 && <p className="mt-3 px-1 text-sm text-muted">No stencil packs match “{stencilSearch}”.</p>}
            </div>
          </ToolbarMenu>

          <ToolbarMenu id="insert" label="Insert" openMenu={openMenu} setOpenMenu={setOpenMenu} active={false} icon={GLYPHS.imagePlus} width="w-72">
            <MenuItem
              onClick={() => {
                fileInputRef.current?.click();
                setOpenMenu(null);
              }}
              swatch={GLYPHS.imagePlus}
              label="Insert image"
            />
            <MenuItem
              onClick={() => {
                addLegend();
                setOpenMenu(null);
              }}
              swatch={GLYPHS.key}
              label="Add legend / key to the plan"
              disabled={value.some((e) => e.kind === "legend")}
            />
            {onBackgroundChange && (
              <>
                <MenuItem
                  onClick={() => {
                    bgInputRef.current?.click();
                    setOpenMenu(null);
                  }}
                  swatch={GLYPHS.image}
                  label={background?.dataUrl ? "Replace background image" : "Set background image"}
                />
                {background?.dataUrl && (
                  <>
                    <div className="flex items-center gap-2.5 px-2 py-1.5">
                      <span className="w-6" />
                      <label className="flex min-w-0 flex-1 items-center gap-2 text-[0.78rem] font-semibold text-muted">
                        Opacity
                        <input
                          type="range"
                          min={12}
                          max={85}
                          value={Math.round(background.opacity * 100)}
                          onChange={(e) => onBackgroundChange({ ...background, opacity: parseInt(e.target.value, 10) / 100 })}
                          className="min-w-0 flex-1 accent-emerald"
                        />
                      </label>
                    </div>
                    <MenuItem
                      onClick={() => {
                        onBackgroundChange(null);
                        setOpenMenu(null);
                      }}
                      swatch={GLYPHS.trash}
                      label={`Remove background — ${background.name}`}
                    />
                  </>
                )}
              </>
            )}
          </ToolbarMenu>

          {/* Hidden file inputs (triggered from the Insert menu) */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="sr-only"
            onChange={async (e) => {
              const input = e.currentTarget; // capture — synthetic event is nulled after await
              const file = input.files?.[0];
              if (file) await insertImage(file);
              input.value = "";
            }}
          />
          {onBackgroundChange && (
            <input
              ref={bgInputRef}
              type="file"
              accept="image/*"
              className="sr-only"
              onChange={async (e) => {
                const input = e.currentTarget; // capture — synthetic event is nulled after await
                const file = input.files?.[0];
                if (file) {
                  const bg = await imageFileToBackground(file);
                  if (bg) onBackgroundChange(bg);
                }
                input.value = "";
              }}
            />
          )}
        </div>
        {/* Status / hint bar */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line bg-white px-3 py-2 text-[0.78rem]">
          <span className="min-w-0 text-muted">
            <span className="font-semibold text-ink">{toolLabel(tool)}</span>
            {toolHint(tool) && <span> — {toolHint(tool)}</span>}
          </span>
          <div className="flex shrink-0 items-center gap-3">
            {mpu !== null && <span className="font-semibold text-emerald-deep">Scale set</span>}
            {savedAt && (
              <span className="text-muted">
                Saved <span className="font-semibold text-ink">{savedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Selected element editing */}
      {selected && (
        <div className="mt-3 flex flex-wrap items-end gap-3 rounded-lg border border-line bg-mist/60 p-3.5">
          <span className="pb-2.5 text-[0.8rem] font-bold uppercase tracking-wide text-muted">{selectedLabel}</span>

          {selected.kind === "label" && (
            <>
              <Field label="Text" className="min-w-[200px]">
                <TextInput value={selected.text} onChange={(e) => updateSelected({ text: e.target.value })} />
              </Field>
              <label className="block">
                <span className="mb-1.5 block text-[0.85rem] font-semibold text-ink">Size</span>
                <input
                  type="range"
                  min={10}
                  max={32}
                  value={selected.fontSize ?? 14}
                  onChange={(e) => updateSelected({ fontSize: parseInt(e.target.value, 10) })}
                  className="w-32 accent-emerald"
                />
              </label>
            </>
          )}

          {selected.kind === "zone" && (
            <>
              <Field label="Zone label" className="min-w-[200px]">
                <TextInput
                  value={selected.label}
                  onChange={(e) => updateSelected({ label: e.target.value })}
                  placeholder="e.g. Crane exclusion — no entry"
                />
              </Field>
              {mpu !== null && (
                <span className="pb-2.5 text-[0.82rem] text-muted">
                  {formatMetres(selected.w, mpu)} × {formatMetres(selected.h, mpu)}
                </span>
              )}
            </>
          )}

          {selected.kind === "icon" && selected.icon === "crane" && (
            <label className="block">
              <span className="mb-1.5 block text-[0.85rem] font-semibold text-ink">
                Exclusion radius{mpu !== null && selected.radius ? ` — ${formatMetres(selected.radius, mpu)}` : ""}
              </span>
              <input
                type="range"
                min={30}
                max={250}
                value={selected.radius ?? 90}
                onChange={(e) => updateSelected({ radius: parseInt(e.target.value, 10) })}
                className="w-44 accent-emerald"
              />
            </label>
          )}

          {selected.kind === "line" && (
            <>
              <label className="flex items-center gap-2 pb-2.5 text-[0.85rem] font-semibold text-ink">
                <input type="checkbox" checked={!!selected.arrow} onChange={(e) => updateSelected({ arrow: e.target.checked || undefined })} className="accent-emerald" />
                Arrow head
              </label>
              <label className="flex items-center gap-2 pb-2.5 text-[0.85rem] font-semibold text-ink">
                <input type="checkbox" checked={!!selected.dash} onChange={(e) => updateSelected({ dash: e.target.checked || undefined })} className="accent-emerald" />
                Dashed
              </label>
              <label className="block">
                <span className="mb-1.5 block text-[0.85rem] font-semibold text-ink">Width</span>
                <input
                  type="range"
                  min={2}
                  max={8}
                  value={selected.width}
                  onChange={(e) => updateSelected({ width: parseInt(e.target.value, 10) })}
                  className="w-28 accent-emerald"
                />
              </label>
            </>
          )}

          {selected.kind === "draw" && (
            <label className="block">
              <span className="mb-1.5 block text-[0.85rem] font-semibold text-ink">Width</span>
              <input
                type="range"
                min={2}
                max={selected.highlight ? 28 : 10}
                value={selected.width}
                onChange={(e) => updateSelected({ width: parseInt(e.target.value, 10) })}
                className="w-28 accent-emerald"
              />
            </label>
          )}

          {selected.kind === "shape" && (
            <label className="flex items-center gap-2 pb-2.5 text-[0.85rem] font-semibold text-ink">
              <input type="checkbox" checked={!!selected.filled} onChange={(e) => updateSelected({ filled: e.target.checked || undefined })} className="accent-emerald" />
              Filled
            </label>
          )}

          {selected.kind === "image" && (
            <label className="block">
              <span className="mb-1.5 block text-[0.85rem] font-semibold text-ink">Opacity</span>
              <input
                type="range"
                min={15}
                max={100}
                value={Math.round((selected.opacity ?? 1) * 100)}
                onChange={(e) => updateSelected({ opacity: parseInt(e.target.value, 10) / 100 })}
                className="w-28 accent-emerald"
              />
            </label>
          )}

          {selected.kind === "calibration" && (
            <Field label="Reference length (m)" className="w-36">
              <TextInput
                type="number"
                min="0.1"
                step="0.1"
                value={String(selected.metres)}
                onChange={(e) => {
                  const m = parseFloat(e.target.value);
                  if (Number.isFinite(m) && m > 0) updateSelected({ metres: m });
                }}
              />
            </Field>
          )}

          {(selected.kind === "zone" || selected.kind === "shape" || selected.kind === "image") && (selected.rotation ?? 0) !== 0 && (
            <button type="button" onClick={() => updateSelected({ rotation: undefined })} className="mb-0.5 text-[0.82rem] font-semibold text-muted underline">
              Reset rotation ({selected.rotation}°)
            </button>
          )}

          {colorEditable && (
            <div className="flex items-center gap-1 pb-2">
              {ELEMENT_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  aria-label={`Set colour ${c}`}
                  className={`h-5 w-5 rounded-full border-2 ${(selected as { color?: string }).color === c ? "scale-110 border-ink" : "border-white"}`}
                  style={{ background: c }}
                  onClick={() => {
                    updateSelected({ color: c } as Partial<PlanElement>);
                    setAnnotColor(c); // new pen/line/shape marks follow the last-picked colour
                  }}
                />
              ))}
              {(selected as { color?: string }).color && (
                <button type="button" onClick={() => updateSelected({ color: undefined } as Partial<PlanElement>)} className="ml-1 text-[0.78rem] font-semibold text-muted underline">
                  Default
                </button>
              )}
            </div>
          )}

          <div className="ml-auto flex items-center gap-2">
            {selected.kind !== "legend" && selected.kind !== "calibration" && (
              <>
                <button type="button" onClick={() => reorderSelected("back")} className="mb-0.5 rounded-full border border-line-2 px-3 py-2 text-[0.78rem] font-semibold text-body hover:border-ink">
                  Send back
                </button>
                <button type="button" onClick={() => reorderSelected("front")} className="mb-0.5 rounded-full border border-line-2 px-3 py-2 text-[0.78rem] font-semibold text-body hover:border-ink">
                  Bring front
                </button>
              </>
            )}
            <button
              type="button"
              onClick={removeSelected}
              className="mb-0.5 rounded-full border border-risk-veryhigh/40 px-3.5 py-2 text-[0.82rem] font-semibold text-[#a31616] hover:bg-[#fdecec]"
            >
              Delete
            </button>
          </div>
        </div>
      )}

      {/* Route / line drafting helper */}
      {(draftRouteType || draftBarrierType) && (
        <p className="mt-3 rounded-lg bg-mist px-3 py-2 text-[0.82rem] text-body">
          Click to add points along the{" "}
          {(draftRouteType ? ROUTE_STYLE[draftRouteType] : BARRIER_STYLE[draftBarrierType!]).label.toLowerCase()}.{" "}
          <button
            type="button"
            onClick={draftRouteType ? finishRoute : finishBarrier}
            disabled={(draftRouteType ? draftRoute : draftBarrier).length < 2}
            className="font-semibold text-emerald-deep underline disabled:opacity-50"
          >
            Finish
          </button>{" "}
          <button
            type="button"
            onClick={() => {
              setDraftRoute([]);
              setDraftBarrier([]);
              setTool("select");
            }}
            className="font-semibold text-muted underline"
          >
            Cancel
          </button>
        </p>
      )}

      {/* Calibration prompt */}
      {pendingCalibration && (
        <div className="mt-3 flex flex-wrap items-end gap-3 rounded-lg border border-emerald/40 bg-emerald-tint px-3.5 py-3">
          <Field label="Real length of the reference line (metres)" className="min-w-[220px]">
            <TextInput
              type="number"
              min="0.1"
              step="0.1"
              value={calibrationMetres}
              onChange={(e) => setCalibrationMetres(e.target.value)}
              placeholder="e.g. 20"
              autoFocus
            />
          </Field>
          <button
            type="button"
            onClick={confirmCalibration}
            disabled={!(parseFloat(calibrationMetres) > 0)}
            className="mb-0.5 rounded-full bg-emerald px-4 py-2 text-[0.82rem] font-semibold text-white disabled:opacity-50"
          >
            Set scale
          </button>
          <button type="button" onClick={() => setPendingCalibration(null)} className="mb-0.5 text-[0.82rem] font-semibold text-muted underline">
            Cancel
          </button>
        </div>
      )}

      {/* Canvas */}
      <div className="mt-3 overflow-hidden rounded-xl border border-line">
        <svg
          ref={svgRef}
          viewBox={`${pan.x} ${pan.y} ${viewW} ${viewH}`}
          className="block w-full touch-none select-none"
          style={{ cursor: tool === "select" ? "default" : tool === "pan" ? "grab" : "crosshair" }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          onDoubleClick={() => {
            if (draftRouteType) finishRoute();
            if (draftBarrierType) finishBarrier();
          }}
        >
          <PlanSvg
            elements={value}
            background={background}
            selectedId={selectedId}
            draftRect={draftRect}
            draftRoute={draftRoute}
            draftRouteType={draftRouteType}
            draftBarrier={draftBarrier}
            draftBarrierType={draftBarrierType}
            draftLine={
              draftLine
                ? {
                    ...draftLine,
                    color: draftLine.purpose === "measure" ? "#0f172a" : draftLine.purpose === "calibrate" ? "#0e7a5c" : annotColor,
                    dash: draftLine.purpose !== "line" && draftLine.purpose !== "arrow",
                  }
                : pendingCalibration
                  ? { ...pendingCalibration, color: "#0e7a5c", dash: true }
                  : null
            }
            draftDraw={draftDraw ? { ...draftDraw, color: annotColor, width: draftDraw.highlight ? 14 : 3 } : null}
            markerPrefix={`e${markerPrefix}`}
          >
            {selectionOverlay}
          </PlanSvg>
        </svg>
      </div>
      <PlanLegend elements={value} />


      <p className="mt-3 text-[0.78rem] text-muted">
        {mpu !== null
          ? "Indicative scale from your reference line — verify all distances on site. Saved on this device only — nothing is sent to us."
          : "The sketch is a diagram, not a scale drawing. It’s saved on this device only — nothing is sent to us."}
      </p>
    </div>
  );
}
